import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2, MapPin, Users } from "lucide-react";
import { getCommunityLogoUrl, getCommunityTypeIcon, getCommunityTypeLabel } from "./communityUtils";

const LOGO_SIZES = {
  xs: "h-6 w-6 rounded-md text-[10px]",
  sm: "h-10 w-10 rounded-xl text-sm",
  md: "h-14 w-14 rounded-2xl text-lg",
  lg: "h-24 w-24 rounded-3xl text-3xl",
};

/** Community logo, or its first letter when there is no logo. Decorative: the name is always shown next to it. */
export const CommunityLogo = ({ community, size = "md", className = "" }) => {
  const url = getCommunityLogoUrl(community);
  const box = LOGO_SIZES[size] || LOGO_SIZES.md;
  return (
    <span
      aria-hidden="true"
      className={`inline-flex shrink-0 items-center justify-center overflow-hidden bg-[#120239] font-bold text-[#FF9200] ring-2 ring-white/40 ${box} ${className}`}
    >
      {url ? (
        <img src={url} alt="" className="h-full w-full object-cover" loading="lazy" />
      ) : (
        (community?.name || "?").charAt(0).toUpperCase()
      )}
    </span>
  );
};

/** Logo + name chip for profiles. Links to the community page. */
export const CommunityBadge = ({ community, dark = false, onClick }) => {
  const { t } = useTranslation();
  if (!community) return null;
  return (
    <Link
      to={`/communities/${community.id}`}
      onClick={onClick}
      className={`inline-flex max-w-full items-center gap-2 rounded-full py-1 pe-3 ps-1 text-xs font-semibold transition ${
        dark
          ? "bg-white/10 text-white ring-1 ring-orange-400/40 hover:bg-white/15"
          : "bg-orange-50 text-[#120239] ring-1 ring-orange-200 hover:bg-orange-100"
      }`}
      title={t("viewCommunity", { defaultValue: "View community" })}
    >
      <CommunityLogo community={community} size="xs" className="rounded-full" />
      <span className="truncate">{community.name}</span>
    </Link>
  );
};

export const CommunityTypeChip = ({ type, dark = false }) => {
  const { t } = useTranslation();
  const Icon = getCommunityTypeIcon(type);
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${
        dark ? "bg-white/10 text-orange-200" : "bg-orange-50 text-orange-700"
      }`}
    >
      <Icon size={12} aria-hidden="true" />
      {getCommunityTypeLabel(type, t)}
    </span>
  );
};

export const CommunityCard = ({ community, onJoin }) => {
  const { t } = useTranslation();
  return (
    <article className="flex h-full flex-col rounded-3xl bg-white p-5 shadow-sm ring-1 ring-gray-200 transition hover:shadow-md">
      <Link to={`/communities/${community.id}`} className="flex items-start gap-4">
        <CommunityLogo community={community} size="md" />
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-lg font-bold text-[#120239]">{community.name}</h3>
          {community.tagline && <p className="mt-0.5 line-clamp-2 text-sm text-gray-500">{community.tagline}</p>}
        </div>
      </Link>

      <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-gray-600">
        <CommunityTypeChip type={community.type} />
        {community.country?.name && (
          <span className="inline-flex items-center gap-1">
            <MapPin size={12} aria-hidden="true" />
            {community.country.name}
          </span>
        )}
        <span className="inline-flex items-center gap-1">
          <Users size={12} aria-hidden="true" />
          {t("communityMembersCount", { defaultValue: "{{count}} members", count: community.memberCount })}
        </span>
      </div>

      <div className="mt-auto pt-5">
        <button
          type="button"
          onClick={() => onJoin(community)}
          className="min-h-[44px] w-full rounded-full bg-[#FF9200] text-sm font-bold text-[#120239] transition hover:bg-orange-400"
        >
          {t("joinCommunity", { defaultValue: "Join Community" })}
        </button>
      </div>
    </article>
  );
};

/** Confirm join / switch / leave. */
export const CommunityConfirmModal = ({ title, body, confirmLabel, danger = false, busy, onConfirm, onClose }) => {
  const { t } = useTranslation();

  useEffect(() => {
    const onKey = (event) => event.key === "Escape" && !busy && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-4 sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="community-confirm-title"
      onClick={() => !busy && onClose()}
    >
      <div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-xl" onClick={(event) => event.stopPropagation()}>
        <h2 id="community-confirm-title" className="text-lg font-bold text-[#120239]">{title}</h2>
        <div className="mt-2 text-sm text-gray-600">{body}</div>
        <div className="mt-6 flex gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="min-h-[44px] flex-1 rounded-full bg-gray-100 text-sm font-semibold text-gray-700 hover:bg-gray-200 disabled:opacity-60"
          >
            {t("cancel", { defaultValue: "Cancel" })}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className={`inline-flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded-full text-sm font-bold disabled:opacity-60 ${
              danger ? "bg-rose-600 text-white hover:bg-rose-700" : "bg-[#FF9200] text-[#120239] hover:bg-orange-400"
            }`}
          >
            {busy && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};
